// Typed client for the vtlite API. Every mutating request carries the X-VTLite header
// (the server's CSRF check); the session cookie is HttpOnly and sent automatically.

import type { ConeObserver, ConeResult, ExperimentConfig, Protocol, RGB, TestExperiment } from "./lib/protocol";
import type { TrialRecord } from "./runner/runner";

export class ApiError extends Error {
  /** The server's `detail` as sent (an object for structured refusals). */
  constructor(readonly status: number, message: string, readonly detail?: unknown) {
    super(message);
  }
}

/** Fired on any 401 except a failed sign-in: the session ended (expired, or signed out
 *  in another tab), so the app shows the sign-in page. */
export const UNAUTHORIZED_EVENT = "vtlite:unauthorized";

async function call<T>(method: string, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { "X-VTLite": "1" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const res = await fetch(url, {
    method, headers, credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = res.statusText;
    let raw: unknown;
    try {
      const j = await res.json();
      raw = j.detail;
      detail = typeof j.detail === "string" ? j.detail
        : typeof j.detail?.message === "string" ? j.detail.message : JSON.stringify(j.detail);
    } catch {
      /* not JSON */
    }
    if (res.status === 401 && !url.startsWith("/api/auth/login") && !url.startsWith("/api/auth/logout")) {
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    }
    throw new ApiError(res.status, detail, raw);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const get = <T>(url: string) => call<T>("GET", url);
const post = <T>(url: string, body?: unknown) => call<T>("POST", url, body ?? {});
const patch = <T>(url: string, body: unknown) => call<T>("PATCH", url, body);
const del = <T>(url: string) => call<T>("DELETE", url);

export type Role = "admin" | "researcher" | "operator" | "patient";

export interface User {
  id: number; username: string; role: Role; display_name: string; email: string;
  site_id: number | null; patient_id: number | null; notify: boolean; is_active: boolean;
  must_change_password: boolean; totp_enabled?: boolean | null; must_setup_2fa?: boolean;
}
export interface Site { id: number; name: string; timezone: string }
export interface Identifiers {
  full_name?: string | null; date_of_birth?: string | null; mrn?: string | null;
  email?: string | null; phone?: string | null; info?: string | null;
}
/** A patient's debug mode (admins): null when off. */
export interface DebugMode {
  skip_checks: boolean; report_on_screen: boolean; report_pdf: boolean; by?: string | null; since?: string | null;
}
export interface PatientSummary {
  id: number; study_id: string; site_id: number; diagnosis: string; created_at: string;
  last_session_at: string | null; program_id: number | null; debug?: DebugMode | null;
}
export interface PatientDetail extends PatientSummary {
  identifiers: Identifiers | null; notes: string; assigned_monitor_serial: string | null;
}
export interface Experiment {
  id: number; name: string; description: string; kind: "acuity" | "contrast"; config: ExperimentConfig;
  version: number; active: boolean; updated_at: string;
  /** Programs whose steps run it, and which versions of it they hold. */
  used_by: Array<{ id: number; name: string; versions: number[]; program_version: number }>;
}
/** An experiment as a file (docs/EXPERIMENT_FORMAT.md). */
export interface ExperimentDocument {
  format: "visualtools-lite/experiment"; format_version: 1; name: string; description: string;
  experiment: ExperimentConfig;
}
export interface ImportCheck {
  ok: boolean; errors: string[]; warnings: string[]; name?: string; document?: ExperimentDocument;
  name_taken?: boolean; same_settings_as?: { id: number; name: string } | null;
}
/** How often a home patient should do one of their programs. */
export interface Schedule {
  kind: "patient_choice" | "every_n_sessions" | "interval";
  n: number; unit: "days" | "weeks" | "months"; grace_days: number;
}
export type ProgramMode = "staff_active" | "schedule" | "patient_picks" | "all";
export interface PlanItem {
  program_id: number; name: string; description: string; position: number; schedule: Schedule; active: boolean;
  status: { last_done_at: string | null; next_due_at: string | null; due: boolean | null;
    overdue_days: number | null; optional: boolean; sessions_until_due?: number };
}
/** A home patient's programs and what to run now (GET /api/me/plan, /api/patients/{id}/programs). */
export interface Plan {
  mode: ProgramMode; items: PlanItem[]; run: number[]; optional: number[]; choose: number[];
  done_this_visit: number[]; visit_id: string | null; next_due_at: string | null;
}
export interface Program {
  id: number; name: string; description: string; protocol: Protocol; version: number;
  is_default: boolean; active: boolean; updated_at: string;
}
export interface Station { id: number; name: string; kind: "clinic" | "home"; site_id: number | null; patient_id: number | null }
export interface DisplayProfile {
  id: number; station_id: number; label: string; edid_sha256: string | null;
  edid_parsed: Record<string, unknown> | null; width_mm: number; height_mm: number;
  width_px: number; height_px: number; size_source: string; browser_label: string;
  model_id: number | null;
}
export interface DisplayModel {
  id: number; name: string; label_pattern: string; width_px: number; height_px: number;
  width_mm: number; height_mm: number; output_bits: number; white_cd_m2: number | null;
  instructions: string; active: boolean; platforms: string[] | null;
  edid_vendor: string | null; edid_product: number | null;
  meter_target: number | null; meter_tolerance_pct: number | null; meter_unit: string | null;
  meter_instructions: string | null;
}
/** A monitor's lux-meter baseline. Patients get it without the value (white_lux). */
export interface LuxBaseline {
  id: number; set_at: string | null; source: "staff_calibrate" | "home_first_session"; set_by_role: string | null;
  monitor: Record<string, any> | null; edid_key: string | null; screen_key: string | null; active: boolean;
  white_lux?: number; readings_lux?: number[]; spread_pct?: number | null; notes?: Record<string, any> | null;
  retired_at?: string | null; set_by?: string | null;
}
/** What the home brightness step needs: check against the baseline, or set the first one. */
export interface LuxNeed {
  required: boolean; phase: "check" | "baseline" | "visit"; unit: "lux";
  tolerance_pct: number; edid_key: string | null; screen_key: string | null;
  baseline: LuxBaseline | null; instructions: string;
}
export interface LuxVerdict {
  verdict: "ok" | "too_low" | "too_high" | "missing" | "implausible" | "no_baseline";
  message: string; mean_lux?: number | null; phase?: string;
}
export interface LuxIdentity {
  monitor: Record<string, any>; edid_key: string | null; screen_key: string | null; edid_read: boolean;
  baseline: LuxBaseline | null; unit: string;
}
export interface MonitorReport {
  id: number; created_at: string; platform: string; os_version: string;
  monitors: Array<Record<string, any>>; extra?: Record<string, any> | null;
}
export interface QualifyCheck {
  key: string; ok: boolean; critical: boolean; label: string; detail: string; fix: string; skipped_for_demo?: boolean;
  /** The patient's requirement for it: info checks never block. */
  level?: Level | "info";
  /** Click-for-click instructions for this computer; "{address}" stands for this site's address. */
  steps?: string[];
}
export interface Qualification {
  qualified: boolean; checks: QualifyCheck[]; model_id: number | null; model_name: string | null;
  ceiling_log_cs: number | null; instructions: string; display_profile_id: number | null; station_id: number | null;
  program: { id: number; name: string; version: number; viewing_distance_mm: number } | null;
  brightness: LuxNeed | null;
  monitor_class?: "approved" | "unapproved" | null;
  monitor_id: { mode: "off" | "optional" | "required"; report: MonitorReport | null;
    steps?: Partial<Record<"mac" | "windows", string[]>> } | null;
  demo?: { by: string | null; since: string | null; size_source?: string; display?: string } | null;
  requirements?: SessionChecks | null;
  /** Recommended checks that did not pass: shown with their fixes; the patient may go on. */
  advised?: string[];
  screen_size_mm?: [number, number] | null; screen_px?: [number, number] | null;
  card?: { level: Level; card_mm: [number, number]; tolerance: number; phase: "check" | "visit" } | null;
}
/** Mandatory: must pass; recommended: offered, may be skipped (noted); off: not required. */
export type Level = "mandatory" | "recommended" | "off";
export interface SetupSteps { chrome: Level; monitor_helper: Level; display: Level; card_size: Level; practice: Level }
export interface MonitorChecks { identity: Level; model: Level; serial: Level; panel_bits: Level; link_bits: Level }
export interface SessionChecks { screen: Level; monitor: MonitorChecks; brightness: Level; distance: Level; card_size: Level }
export interface HomeRequirements { setup: SetupSteps; session: SessionChecks }
export type SetupStepKey = keyof SetupSteps;
export interface SetupStatus {
  steps: Array<{ key: SetupStepKey; title: string; level: Level;
    status: "done" | "skipped" | "todo" | "not_required" | "before_setup"; at: string | null; detail: Record<string, any> | null }>;
  needed: boolean; blocking: SetupStepKey[]; requirements: HomeRequirements; own_requirements: boolean;
  reset_at: string | null; completed_at: string | null; card_mm: [number, number]; card_tolerance: number;
  helper_steps?: Partial<Record<"mac" | "windows", string[]>>;
  program_defaults?: HomeRequirements; serial_set?: boolean;
}
export interface Calibration {
  id: number; display_profile_id: number; tier: "measured" | "model" | "nominal" | "unverified";
  white_cd_m2: number | null; black_cd_m2: number | null; output_bits: number; os_mode: string;
  instrument: string; step_test: Record<string, unknown> | null; lut_rel: number[]; created_at: string;
}
export interface TestInfo {
  id: number; order: number; kind: "acuity" | "contrast"; eye: "OD" | "OS" | "OU";
  config: Record<string, unknown>; status: string; result: Record<string, any> | null;
  display_snapshot?: Record<string, any> | null; compliance?: { events: Array<Record<string, any>> } | null;
  retry_of?: number | null; purpose?: "main" | "repeat" | "confirm" | null;
  experiment?: TestExperiment | null;
  started_at?: string | null; scored_at?: string | null;
}
export interface SessionInfo {
  id: number; patient_id: number; mode: "clinic" | "home"; station_id: number | null;
  display_profile_id: number | null; calibration_id: number | null; calibration_tier: string;
  viewing_distance_mm: number; protocol: Protocol; program_id?: number | null; visit_id?: string | null;
  qualification?: Record<string, any> | null;
  status: string; started_at: string;
  completed_at: string | null;
  // `screen` and `brightness_end`: only sessions recorded when the lux meter was read again
  // after the last test (now one reading per session, before the first test)
  change_summary: { flags: Array<Record<string, unknown>>; screen?: { status: string; urgent: boolean; text: string;
    drift_from_start_pct?: number | null; drift_from_baseline_pct?: number | null } } | null;
  brightness_end?: Record<string, any> | null;
  tests: TestInfo[];
}
export interface HistorySeries {
  eye: string; kind: "acuity" | "contrast"; condition?: string; measure?: string;
  points: Array<{ session_id: number; when: string; value: number; mode: string; calibration_tier: string }>;
  assessment: { status: string; baseline: number | null; latest: number | null; delta: number | null; cor: number;
    trend: Record<string, unknown> | null; alert: "none" | "repeat" | "contact"; baseline_from: string | null };
}
export interface Summary {
  session_id: number; date: string; mode: string; note: string;
  results: Array<{ eye: string; eye_label: string; acuity?: SummaryItem; contrast?: SummaryItem;
    items?: Array<SummaryItem & { kind: "acuity" | "contrast"; name: string | null; repeat: number | null; times: number | null; purpose: string }> }>;
  alerts: Array<{ eye: string; eye_label: string; kind: string; message: string }>;
  contact_message: string | null; demo?: boolean; demo_note?: string | null;
  /** What of the comprehensive report this viewer may see (patients: their debug mode). */
  debug_access?: { on_screen: boolean; pdf: boolean };
}
export interface SummaryItem {
  available: boolean; invalid?: boolean; text: string; comparison?: string | null; logmar?: number; log_cs?: number;
  alert?: "none" | "repeat" | "contact"; demo?: boolean;
}

export type DeleteKind = "patients" | "sessions" | "tests" | "programs" | "sites" | "stations" | "displays"
  | "display_models" | "users";
export type Targets = Partial<Record<DeleteKind, number[]>>;
export interface DeletePreview {
  cascade: boolean; selected: number; will_delete: number; confirm_phrase: string;
  refused: Array<{ kind: DeleteKind; id: number; label: string; reason: string; needs_cascade: boolean }>;
  cascade_would_help: boolean;
  totals: Record<DeleteKind, number> & { trials: number };
  sessions_of_patients_who_stay: { sessions: number; patients: number };
  notes: string[]; fingerprint: string;
}
export interface SessionRow {
  id: number; patient_id: number; study_id: string; site_id: number; started_at: string; completed_at: string | null;
  mode: "clinic" | "home"; status: string; program: string | null; demo: boolean; patient_demo: boolean;
  calibration_tier: string; tests: number; counted: number;
}

export const api = {
  login: (username: string, password: string, code?: string) =>
    post<User>("/api/auth/login", { username, password, code: code || null }),
  twoFactorSetup: () => post<{ secret: string; otpauth_uri: string; qr_svg: string }>("/api/auth/2fa/setup"),
  twoFactorEnable: (code: string) => post<User>("/api/auth/2fa/enable", { code }),
  twoFactorDisable: (password: string, code: string) => post<User>("/api/auth/2fa/disable", { password, code }),
  adminSettings: () => get<{ require_2fa_staff: boolean }>("/api/admin/settings"),
  setAdminSettings: (body: { require_2fa_staff: boolean }) => call<{ require_2fa_staff: boolean }>("PUT", "/api/admin/settings", body),
  logout: () => post<void>("/api/auth/logout"),
  me: () => get<User>("/api/auth/me"),
  changePassword: (current_password: string, new_password: string) =>
    post<void>("/api/auth/change-password", { current_password, new_password }),

  sites: () => get<Site[]>("/api/sites"),
  createSite: (name: string) => post<Site>("/api/sites", { name }),
  users: () => get<User[]>("/api/users"),
  createUser: (body: Record<string, unknown>) =>
    post<{ user: User; temporary_password: string | null }>("/api/users", body),
  updateUser: (id: number, body: Record<string, unknown>) =>
    patch<{ user: User; temporary_password: string | null }>(`/api/users/${id}`, body),

  patients: () => get<PatientSummary[]>("/api/patients"),
  patient: (id: number) => get<PatientDetail>(`/api/patients/${id}`),
  createPatient: (body: { study_id: string; site_id?: number | null; identifiers: Identifiers | null; diagnosis?: string;
    notes?: string; program_id?: number | null; assigned_monitor_serial?: string | null }) =>
    post<PatientDetail>("/api/patients", body),
  updatePatient: (id: number, body: Record<string, unknown>) => patch<PatientDetail>(`/api/patients/${id}`, body),
  setDebugMode: (id: number, body: { enabled: boolean; skip_checks: boolean; report_on_screen: boolean; report_pdf: boolean }) =>
    call<PatientDetail>("PUT", `/api/patients/${id}/debug-mode`, body),

  // Admin only, permanent. A refusal (409) says what still depends on the record.
  deletePatient: (id: number, studyId: string) =>
    del<{ deleted: boolean; sessions: number }>(`/api/patients/${id}?confirm=${encodeURIComponent(studyId)}`),
  deleteSite: (id: number) => del<{ deleted: boolean }>(`/api/sites/${id}`),
  deleteUser: (id: number) => del<{ deleted: boolean }>(`/api/users/${id}`),
  deleteStation: (id: number) => del<{ deleted: boolean }>(`/api/stations/${id}`),
  deleteDisplay: (id: number) => del<{ deleted: boolean }>(`/api/displays/${id}`),
  deleteDisplayModel: (id: number) => del<{ deleted: boolean }>(`/api/display-models/${id}`),
  deleteProgram: (id: number) => del<{ deleted: boolean }>(`/api/programs/${id}`),

  // Many at once (admin): preview what goes and what is refused, then delete with "delete N".
  deletePreview: (targets: Targets, cascade: boolean) =>
    post<DeletePreview>("/api/admin/delete/preview", { targets, cascade }),
  deleteMany: (targets: Targets, cascade: boolean, confirm: string, fingerprint: string) =>
    post<{ deleted: Record<string, number>; items: number; batch: string }>("/api/admin/delete",
      { targets, cascade, confirm, fingerprint }),
  allSessions: () => get<SessionRow[]>("/api/admin/sessions"),
  counts: () => get<Record<DeleteKind, number>>("/api/admin/counts"),

  experiments: () => get<Experiment[]>("/api/experiments"),
  experiment: (id: number) => get<Experiment>(`/api/experiments/${id}`),
  createExperiment: (body: { name: string; description: string; config: ExperimentConfig; active?: boolean }) =>
    post<Experiment>("/api/experiments", body),
  updateExperiment: (id: number, body: { name: string; description: string; config: ExperimentConfig; active: boolean }) =>
    call<Experiment>("PUT", `/api/experiments/${id}`, body),
  applyExperiment: (id: number, programIds: number[]) =>
    post<{ updated: Array<{ id: number; name: string; version: number }> }>(`/api/experiments/${id}/apply`, { program_ids: programIds }),
  deleteExperiment: (id: number) => del<{ deleted: boolean }>(`/api/experiments/${id}`),
  experimentFileUrl: (id: number) => `/api/experiments/${id}/file`,
  checkExperimentImport: (document: unknown, name?: string) =>
    post<ImportCheck>("/api/experiments/import/check", { document, name: name ?? null }),
  importExperiment: (document: unknown, name?: string) =>
    post<Experiment>("/api/experiments/import", { document, name: name ?? null }),
  programs: () => get<Program[]>("/api/programs"),
  program: (id: number) => get<Program>(`/api/programs/${id}`),
  createProgram: (body: { name: string; description: string; protocol: Protocol; active?: boolean }) =>
    post<Program>("/api/programs", body),
  updateProgram: (id: number, body: { name: string; description: string; protocol: Protocol; active: boolean }) =>
    call<Program>("PUT", `/api/programs/${id}`, body),
  makeDefaultProgram: (id: number) => post<Program>(`/api/programs/${id}/default`),
  history: (id: number) => get<{ patient_id: number; series: HistorySeries[] }>(`/api/patients/${id}/history`),
  patientSessions: (id: number) => get<SessionInfo[]>(`/api/patients/${id}/sessions`),

  stations: () => get<Station[]>("/api/stations"),
  createStation: (body: { name: string; kind?: string; site_id?: number | null; patient_id?: number | null }) =>
    post<Station>("/api/stations", body),
  displays: (stationId: number) => get<DisplayProfile[]>(`/api/stations/${stationId}/displays`),
  addDisplay: (stationId: number, body: Record<string, unknown>) =>
    post<DisplayProfile>(`/api/stations/${stationId}/displays`, body),
  display: (id: number) => get<DisplayProfile>(`/api/displays/${id}`),
  parseEdid: (edid: string) => post<Record<string, any>>("/api/edid/parse", { edid }),
  addCalibration: (displayId: number, body: Record<string, unknown>) =>
    post<Calibration>(`/api/displays/${displayId}/calibrations`, body),
  latestCalibration: (displayId: number) => get<Calibration>(`/api/displays/${displayId}/calibrations/latest`),

  defaultProtocol: () => get<Protocol>("/api/protocols/default"),
  createSession: (body: Record<string, unknown>) => post<SessionInfo>("/api/sessions", body),
  session: (id: number) => get<SessionInfo>(`/api/sessions/${id}`),
  startTest: (id: number, config: unknown, display: unknown) => post<TestInfo>(`/api/tests/${id}/start`, { config, display }),
  addTrials: (id: number, trials: TrialRecord[]) =>
    post<{ added: number; stored: number }>(`/api/tests/${id}/trials`, { trials }),
  completeTest: (id: number, aborted = false, compliance: unknown = null) =>
    post<TestInfo>(`/api/tests/${id}/complete`, { aborted, compliance }),
  retryTest: (id: number) => post<TestInfo>(`/api/tests/${id}/retry`),
  completeSession: (id: number) => post<SessionInfo>(`/api/sessions/${id}/complete`),
  summary: (id: number) => get<Summary>(`/api/sessions/${id}/summary`),
  reportUrl: (id: number, debug = false) => `/api/sessions/${id}/report.pdf${debug ? "?debug=1" : ""}`,
  trialsCsvUrl: (id: number) => `/api/sessions/${id}/trials.csv`,
  sessionDebug: (id: number) => get<Record<string, any>>(`/api/sessions/${id}/debug`),

  displayModels: () => get<DisplayModel[]>("/api/display-models"),
  createDisplayModel: (body: { display_id: number; name: string; label_pattern: string; instructions?: string;
    platforms?: string[] }) =>
    post<DisplayModel>("/api/display-models", body),
  setDisplayModelActive: (id: number, active: boolean) => post<DisplayModel>(`/api/display-models/${id}/active?active=${active}`),
  updateDisplayModel: (id: number, body: Record<string, unknown>) => patch<DisplayModel>(`/api/display-models/${id}`, body),
  qualify: (probe: unknown, opts: { program_id?: number | null; visit_id?: string | null } = {}) =>
    post<Qualification>("/api/qualify", { probe, ...opts }),
  myPlan: (visitId?: string | null) => get<Plan>(`/api/me/plan${visitId ? `?visit_id=${encodeURIComponent(visitId)}` : ""}`),
  patientPrograms: (patientId: number) => get<Plan>(`/api/patients/${patientId}/programs`),
  setPatientPrograms: (patientId: number, body: { mode: ProgramMode; active_program_id: number | null;
    items: Array<{ program_id: number; schedule: Schedule }> }) => call<Plan>("PUT", `/api/patients/${patientId}/programs`, body),
  conePreview: (body: { observer: ConeObserver; mode: string; center: string; background_rgb_rel: RGB; display_id?: number | null }) =>
    post<ConeResult>("/api/cone-isolation/preview", body),
  brightnessCheck: (readings_lux: number[], keys: { edid_key: string | null; screen_key: string | null }) =>
    post<LuxVerdict>("/api/brightness-check", { readings_lux, ...keys }),
  luxIdentify: (probe: unknown, target: { patient_id?: number; display_id?: number }) =>
    post<LuxIdentity>("/api/lux/identify", { probe, ...target }),
  patientBaselines: (patientId: number) => get<LuxBaseline[]>(`/api/patients/${patientId}/lux-baselines`),
  displayBaselines: (displayId: number) => get<LuxBaseline[]>(`/api/displays/${displayId}/lux-baselines`),
  setBaseline: (target: { patient_id?: number; display_id?: number }, body: {
    readings_lux: number[]; edid_key: string | null; screen_key: string | null;
    monitor?: Record<string, unknown>; notes?: Record<string, unknown> }) =>
    post<LuxBaseline & { message: string }>(target.patient_id !== undefined
      ? `/api/patients/${target.patient_id}/lux-baselines` : `/api/displays/${target.display_id}/lux-baselines`, body),
  monitorHelperUrl: (platform: "mac" | "windows") => `/api/monitor-helper/${platform}`,
  mySetup: () => get<SetupStatus>("/api/me/setup"),
  setupStep: (step: SetupStepKey, body: { skip?: boolean; probe?: unknown; readings_lux?: number[]; scale?: number;
    practice?: Record<string, unknown> }) => post<SetupStatus>(`/api/me/setup/${step}`, body),
  homeRequirements: (patientId: number) => get<SetupStatus>(`/api/patients/${patientId}/home-requirements`),
  setHomeRequirements: (patientId: number, requirements: HomeRequirements | null) =>
    call<SetupStatus>("PUT", `/api/patients/${patientId}/home-requirements`, { requirements }),
  resetSetup: (patientId: number) => post<SetupStatus>(`/api/patients/${patientId}/setup/reset`),
  monitorLatest: (patientId?: number) =>
    get<{ report: MonitorReport | null }>(`/api/monitor-id/latest${patientId ? `?patient_id=${patientId}` : ""}`),
  /** Staff: the latest report from this account's own helper download, with the raw EDIDs. */
  myMonitorEdids: () => get<{ report: MonitorReport | null }>("/api/monitor-id/latest?with_edid=true"),

  notifications: () => get<Array<{ id: number; created_at: string; kind: string; session_id: number | null; message: string; read: boolean }>>("/api/notifications"),
  markRead: (id: number) => post<void>(`/api/notifications/${id}/read`),
};
